import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth, useLanguage, api } from '../context/AppContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import {
  Trophy, Loader2, Star, Lock, Share2, Flame, Users,
  ChevronRight, Zap, Target, Crown, Award
} from 'lucide-react';
import { toast } from 'sonner';

// ============ CONFETTI ANIMATION ============
function CelebrationOverlay({ badge, onClose }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles = [];
    const colors = ['#D4FF00', '#FF6B35', '#A855F7', '#3B82F6', '#EF4444', '#22C55E', '#F59E0B', '#06B6D4'];

    for (let i = 0; i < 150; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: -20 - Math.random() * 200,
        size: Math.random() * 8 + 3,
        color: colors[Math.floor(Math.random() * colors.length)],
        speedY: Math.random() * 4 + 2,
        speedX: (Math.random() - 0.5) * 3,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 10,
      });
    }

    let animId;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      let active = false;
      for (const p of particles) {
        p.y += p.speedY;
        p.x += p.speedX;
        p.rotation += p.rotationSpeed;
        if (p.y < canvas.height + 20) active = true;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
        ctx.restore();
      }
      if (active) {
        animId = requestAnimationFrame(animate);
      }
    };
    animate();

    const timer = setTimeout(onClose, 4000);
    return () => { cancelAnimationFrame(animId); clearTimeout(timer); };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" onClick={onClose} data-testid="celebration-overlay">
      <canvas ref={canvasRef} className="absolute inset-0" />
      <div className="relative z-10 bg-card/95 backdrop-blur-xl border-2 border-primary rounded-2xl p-8 max-w-sm mx-4 text-center animate-in zoom-in-95 duration-500">
        {badge?.badge_image ? (
          <img src={badge.badge_image} alt={badge.name} className="w-24 h-24 mx-auto mb-4 object-contain animate-bounce drop-shadow-[0_0_15px_rgba(239,68,68,0.5)]" />
        ) : (
          <div className="text-6xl mb-4 animate-bounce">{badge?.icon}</div>
        )}
        <h2 className="font-heading text-2xl uppercase tracking-tight text-primary mb-2">
          {badge?.name}
        </h2>
        <p className="text-sm text-muted-foreground mb-4">{badge?.description}</p>
        <div className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase ${
          badge?.rarity === 'legendary' ? 'bg-yellow-500/20 text-yellow-400' :
          badge?.rarity === 'epic' ? 'bg-purple-500/20 text-purple-400' :
          badge?.rarity === 'rare' ? 'bg-blue-500/20 text-blue-400' :
          'bg-green-500/20 text-green-400'
        }`}>
          {badge?.rarity}
        </div>
        <p className="text-xs text-primary mt-4 font-heading uppercase tracking-wider">
          Niveau superieur atteint!
        </p>
      </div>
    </div>
  );
}

// ============ BADGE CARD ============
function BadgeCard({ badge, earned, language, onShare }) {
  const [sharing, setSharing] = useState(false);
  const handleShare = async (e) => {
    e.stopPropagation();
    if (sharing) return;
    setSharing(true);
    try {
      await onShare(badge);
    } finally {
      setSharing(false);
    }
  };
  return (
    <div
      className={`relative p-4 rounded-xl border-2 transition-all ${
        earned
          ? 'bg-card border-primary/50 hover:border-primary'
          : 'bg-secondary/30 border-border/30 opacity-60'
      }`}
      data-testid={`badge-${badge.id}`}
    >
      <div className="text-center">
        <div className={`mb-2 ${earned ? '' : 'grayscale opacity-40'}`}>
          {earned ? (
            badge.badge_image ? (
              <img src={badge.badge_image} alt={badge.name} className="w-14 h-14 mx-auto object-contain drop-shadow-[0_0_8px_rgba(239,68,68,0.3)]" />
            ) : (
              <span className="text-4xl">{badge.icon}</span>
            )
          ) : (
            <Lock className="w-8 h-8 mx-auto text-muted-foreground" />
          )}
        </div>
        <p className="text-sm font-heading uppercase tracking-tight text-foreground truncate">
          {badge.name}
        </p>
        <p className="text-[10px] text-muted-foreground mt-1 line-clamp-2">
          {badge.description}
        </p>
        <div className={`mt-2 inline-block px-2 py-0.5 rounded-full text-[9px] font-bold uppercase ${
          badge.rarity === 'legendary' ? 'bg-yellow-500/20 text-yellow-400' :
          badge.rarity === 'epic' ? 'bg-purple-500/20 text-purple-400' :
          badge.rarity === 'rare' ? 'bg-blue-500/20 text-blue-400' :
          badge.rarity === 'uncommon' ? 'bg-green-500/20 text-green-400' :
          'bg-gray-500/20 text-gray-400'
        }`}>
          {badge.rarity}
        </div>
        {earned && badge.earned_at && (
          <p className="text-[9px] text-muted-foreground mt-1">
            {new Date(badge.earned_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}
          </p>
        )}
        {earned && onShare && (
          <Button
            onClick={handleShare}
            disabled={sharing}
            size="sm"
            variant="outline"
            className="mt-2 h-7 w-full text-[10px] font-heading uppercase tracking-wider border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground"
            data-testid={`share-badge-${badge.id}`}
          >
            {sharing ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <>
                <Share2 className="w-3 h-3 mr-1" />
                {language === 'fr' ? 'Partager' : 'Share'}
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}

// ============ COMMUNITY CHALLENGE BAR ============
function CommunityChallenge({ language }) {
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/challenges/collective/active')
      .then(res => setChallenge(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!challenge?.has_active) return null;

  const ch = challenge.challenge;
  const progressPct = Math.min(100, Math.round(((ch.global_progress || 0) / (ch.global_target || 1)) * 100));

  return (
    <Card className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border-orange-500/30 mb-6" data-testid="community-challenge">
      <CardContent className="p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-orange-500/20 rounded-full flex items-center justify-center">
            <Flame className="w-5 h-5 text-orange-400" />
          </div>
          <div className="flex-1">
            <p className="text-xs font-heading uppercase tracking-wider text-orange-400">
              {language === 'fr' ? 'Défi de la Semaine' : 'Weekly Challenge'}
            </p>
            <h3 className="text-sm font-bold text-foreground">{ch.title}</h3>
          </div>
          <Users className="w-5 h-5 text-muted-foreground" />
          <span className="text-sm font-bold text-foreground">{(ch.participants || []).length}</span>
        </div>

        {ch.description && (
          <p className="text-xs text-muted-foreground mb-3">{ch.description}</p>
        )}

        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-xs text-muted-foreground">
              {language === 'fr' ? 'La communaute Extra Miles' : 'Extra Miles community'}
            </span>
            <span className={`text-sm font-black ${progressPct >= 75 ? 'text-green-400' : progressPct >= 50 ? 'text-yellow-400' : 'text-orange-400'}`}>
              {progressPct}%
            </span>
          </div>
          <Progress
            value={progressPct}
            className={`h-3 ${
              progressPct >= 75 ? '[&>div]:bg-gradient-to-r [&>div]:from-green-500 [&>div]:to-emerald-400' :
              progressPct >= 50 ? '[&>div]:bg-gradient-to-r [&>div]:from-yellow-500 [&>div]:to-orange-400' :
              '[&>div]:bg-gradient-to-r [&>div]:from-orange-500 [&>div]:to-red-400'
            }`}
          />
          <p className="text-center text-xs font-heading uppercase tracking-wider text-muted-foreground">
            {progressPct >= 100
              ? (language === 'fr' ? 'MISSION ACCOMPLIE!' : 'MISSION COMPLETE!')
              : (language === 'fr' ? `L'equipe est a ${progressPct}% de l'objectif!` : `The team is at ${progressPct}% of the goal!`)}
          </p>
        </div>

        {/* User progress */}
        {challenge.user_progress && (
          <div className="mt-3 pt-3 border-t border-orange-500/20">
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted-foreground">{language === 'fr' ? 'Ta contribution' : 'Your contribution'}</span>
              <span className="text-xs font-bold text-primary">{challenge.user_progress.progress || 0} pts</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============ TROPHY HUNTERS LEADERBOARD ============
function TrophyHuntersLeaderboard({ language, currentUserId }) {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/badges/leaderboard')
      .then(res => setLeaderboard(res.data.leaderboard))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (leaderboard.length === 0) return null;

  const medals = ['text-yellow-400', 'text-gray-400', 'text-orange-400'];

  return (
    <Card className="bg-card border-border mb-6" data-testid="trophy-hunters-leaderboard">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-heading uppercase tracking-wider flex items-center gap-2">
          <Crown className="w-4 h-4 text-yellow-400" />
          {language === 'fr' ? 'Chasseurs de Trophees' : 'Trophy Hunters'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {leaderboard.map((p) => {
          const isMe = p.user_id === currentUserId;
          return (
            <div
              key={p.user_id}
              className={`flex items-center gap-3 p-2.5 rounded-lg transition-all ${isMe ? 'bg-primary/10 border border-primary/30' : 'bg-secondary/30'}`}
              data-testid={`hunter-${p.rank}`}
            >
              <span className={`text-lg font-black w-7 text-center ${p.rank <= 3 ? medals[p.rank - 1] : 'text-muted-foreground'}`}>
                {p.rank <= 3 ? ['👑', '🥈', '🥉'][p.rank - 1] : `#${p.rank}`}
              </span>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-bold truncate ${isMe ? 'text-primary' : 'text-foreground'}`}>
                  {p.name}{isMe ? (language === 'fr' ? ' (toi)' : ' (you)') : ''}
                </p>
              </div>
              <div className="flex items-center gap-1.5">
                <Trophy className="w-3.5 h-3.5 text-yellow-400" />
                <span className="text-sm font-black text-foreground">{p.badge_count}</span>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

// ============ MAIN PAGE ============
export default function Victories() {
  const { user } = useAuth();
  const { language, t } = useLanguage();
  const [catalog, setCatalog] = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [celebration, setCelebration] = useState(null);
  const [checking, setChecking] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [catalogRes, progressRes] = await Promise.all([
        api.get('/badges/catalog'),
        api.get('/badges/progress'),
      ]);
      setCatalog(catalogRes.data.badges || []);
      setProgress(progressRes.data.progress || []);
    } catch {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const checkNewBadges = async () => {
    setChecking(true);
    try {
      const res = await api.post('/badges/check');
      if (res.data.new_badges && res.data.new_badges.length > 0) {
        setCelebration(res.data.new_badges[0]);
        fetchData();
      } else {
        toast.info(language === 'fr' ? 'Aucun nouveau trophee pour le moment!' : 'No new trophies yet!');
      }
    } catch {
      toast.error('Erreur');
    } finally {
      setChecking(false);
    }
  };

  const shareBadge = async (badge) => {
    try {
      const res = await api.post('/badges/share', { badge_id: badge.id });
      // Backend returns share_url = preview endpoint (has OG tags for social crawlers)
      const shareUrl = `${window.location.origin}${res.data.share_url}`;
      const shareTitle = language === 'fr' ? `J'ai debloque le trophee ${badge.name} sur YUMIX!` : `I unlocked the ${badge.name} trophy on YUMIX!`;
      const shareText = badge.description || '';

      if (navigator.share) {
        try {
          await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
          return;
        } catch (err) {
          if (err?.name === 'AbortError') return;
        }
      }
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success(language === 'fr' ? 'Lien copie dans le presse-papier!' : 'Link copied to clipboard!');
      } catch {
        window.prompt(language === 'fr' ? 'Copie ce lien:' : 'Copy this link:', shareUrl);
      }
    } catch {
      toast.error(language === 'fr' ? 'Erreur partage' : 'Share error');
    }
  };

  const [activeSection, setActiveSection] = useState('all');

  const sectionCounts = {
    all: catalog.length,
    marche: catalog.filter(b => b.section === 'marche').length,
    forge: catalog.filter(b => b.section === 'forge').length,
    carburant: catalog.filter(b => b.section === 'carburant').length,
    citadelle: catalog.filter(b => b.section === 'citadelle').length,
    grades: catalog.filter(b => b.section === 'grades').length,
  };

  const filteredCatalog = activeSection === 'all'
    ? catalog
    : catalog.filter(b => b.section === activeSection);

  const earnedBadges = filteredCatalog.filter(b => b.earned);
  const lockedBadges = filteredCatalog.filter(b => !b.earned);

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center py-20">
          <Loader2 className="w-10 h-10 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-6" data-testid="victories-page">
        {/* Celebration overlay */}
        {celebration && (
          <CelebrationOverlay badge={celebration} onClose={() => setCelebration(null)} />
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl uppercase tracking-tight text-foreground flex items-center gap-3">
              <Trophy className="w-8 h-8 text-yellow-400" />
              {t('modals.victories_title')}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {catalog.filter(b => b.earned).length}/{catalog.length} {language === 'fr' ? 'trophees debloques' : 'trophies unlocked'}
            </p>
          </div>
          <Button
            onClick={checkNewBadges}
            disabled={checking}
            size="sm"
            className="bg-primary text-primary-foreground font-heading uppercase"
            data-testid="check-badges-btn"
          >
            {checking ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Zap className="w-4 h-4 mr-1.5" />}
            {language === 'fr' ? 'Verifier' : 'Check'}
          </Button>
        </div>

        {/* Community Challenge */}
        <CommunityChallenge language={language} />

        {/* Trophy Hunters Leaderboard */}
        <TrophyHuntersLeaderboard language={language} currentUserId={user?.user_id} />

        {/* Category tabs — all categories centralized */}
        <div className="flex gap-1.5 overflow-x-auto pb-2 mb-5 -mx-4 px-4 scrollbar-hide" data-testid="badge-category-tabs">
          {[
            { id: 'all', labelFr: 'Tous', labelEn: 'All', emoji: '🏅' },
            { id: 'marche', labelFr: 'Marche', labelEn: 'Walking', emoji: '🥾' },
            { id: 'forge', labelFr: 'Forge', labelEn: 'Forge', emoji: '💪' },
            { id: 'carburant', labelFr: 'Carburant', labelEn: 'Fuel', emoji: '🍽️' },
            { id: 'citadelle', labelFr: 'Citadelle', labelEn: 'Citadel', emoji: '🏰' },
            { id: 'grades', labelFr: 'Grades', labelEn: 'Ranks', emoji: '🎖️' },
          ].map(tab => {
            const active = activeSection === tab.id;
            const count = sectionCounts[tab.id] || 0;
            if (count === 0) return null;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id)}
                className={`shrink-0 px-3 py-1.5 rounded-full border text-xs font-heading uppercase tracking-wider transition-all flex items-center gap-1.5 ${
                  active
                    ? 'bg-primary text-primary-foreground border-primary shadow-md'
                    : 'bg-card border-border text-muted-foreground hover:text-foreground hover:border-primary/40'
                }`}
                data-testid={`badge-tab-${tab.id}`}
              >
                <span>{tab.emoji}</span>
                <span>{language === 'fr' ? tab.labelFr : tab.labelEn}</span>
                <span className={`text-[9px] px-1.5 rounded-full ${active ? 'bg-primary-foreground/20' : 'bg-secondary'}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Earned Badges */}
        {earnedBadges.length > 0 && (          <div className="mb-8">
            <h2 className="text-sm font-heading uppercase tracking-wider text-primary mb-3 flex items-center gap-2">
              <Award className="w-4 h-4" />
              {language === 'fr' ? 'Trophees Debloques' : 'Unlocked Trophies'} ({earnedBadges.length})
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="earned-badges-grid">
              {earnedBadges.map(b => (
                <BadgeCard key={b.id} badge={b} earned language={language} onShare={shareBadge} />
              ))}
            </div>
          </div>
        )}

        {/* Progress towards next badges */}
        {progress.length > 0 && (
          <div className="mb-8">
            <h2 className="text-sm font-heading uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
              <Target className="w-4 h-4" />
              {language === 'fr' ? 'En Progression' : 'In Progress'}
            </h2>
            <div className="space-y-3" data-testid="progress-list">
              {progress.map(p => (
                <Card key={p.id} className="bg-card border-border" data-testid={`progress-${p.id}`}>
                  <CardContent className="p-3 flex items-center gap-3">
                    <span className="text-2xl">{p.icon}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-foreground truncate">{p.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Progress value={p.progress_percent} className="h-2 flex-1" />
                        <span className="text-xs font-bold text-muted-foreground shrink-0">
                          {p.current}/{p.target}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Locked Badges */}
        {lockedBadges.length > 0 && (
          <div className="mb-8">
            <h2 className="text-sm font-heading uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
              <Lock className="w-4 h-4" />
              {language === 'fr' ? 'A Débloquer' : 'Locked'} ({lockedBadges.length})
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="locked-badges-grid">
              {lockedBadges.map(b => (
                <BadgeCard key={b.id} badge={b} earned={false} language={language} />
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
